import * as Updates from 'expo-updates';
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { BigButton } from '@components/Button';
import Icon from '@components/Icon';
import Image from '@components/Image';
import { Paragraph, Title } from '@components/Typography';
import { Row } from '@components/View';
import { useAppTheme } from '@context/ThemeContext';
import { logError, logEvent } from '@helpers/Loggers';

export type ErrorProps = {
  tapToReload?: boolean;
  message?: string;
  onRetry?: () => Promise<unknown> | unknown;
};

type ErrorViewProps = React.ComponentProps<typeof View> & ErrorProps;

export default function ErrorView({
  message,
  onRetry,
  tapToReload = true,
  style,
  ...props
}: ErrorViewProps) {
  React.useEffect(() => {
    logEvent('error_shown', { message });
  }, [message]);

  const appTheme = useAppTheme();

  const onPressRetry = () => {
    Promise.resolve(onRetry?.()).catch((error) => logError(error));
  };

  const onPressReload = () => {
    Promise.resolve(Updates.reloadAsync()).catch((error) => logError(error));
  };

  const shouldShowRetry = Boolean(onRetry);
  const shouldShowReload = tapToReload && Platform.OS !== 'web';
  const defaultMessage = 'Please check your internet connection and try again.';

  const errorSize = 256;

  return (
    <View
      style={[styles.container, { backgroundColor: appTheme.colors.background }, style]}
      {...props}>
      <Image
        source={require('../assets/images/error.jpeg')}
        style={[{ height: errorSize, width: errorSize, borderRadius: errorSize }, styles.image]}
      />
      <Row>
        <Icon name="alert-circle-outline" color={appTheme.colors.error} />
        <Title style={styles.title}>Error</Title>
      </Row>
      <Paragraph style={styles.paragraph}>{message ? message : defaultMessage}</Paragraph>
      {shouldShowRetry ? (
        <BigButton onPress={onPressRetry} icon="reload" mode="contained" uppercase={false}>
          Retry
        </BigButton>
      ) : null}
      {shouldShowReload ? (
        <BigButton onPress={onPressReload} icon="restart" mode="outlined" uppercase={false}>
          Reload
        </BigButton>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    padding: 20,
    width: '100%',
  },
  image: {
    margin: 10,
  },
  paragraph: {
    padding: 10,
    textAlign: 'center',
  },
  title: {
    paddingHorizontal: 10,
  },
});
